import { getCascadeFlatOption, noButtons, Text } from '@byecode/ui'
import type { FilterBlockValue, FilterOption, NumberRangeArray } from '@lighthouse/core'
import { FilterSelectWay, FilterWay } from '@lighthouse/core'
import {
    CascadeDrawer,
    CheckButton,
    isDateValue,
    TooltipText,
    useApplicationContext,
    useLabelStyle,
    useLanguageContext
} from '@lighthouse/shared'
import { lightFormat } from 'date-fns'
import React, { useCallback, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'

import { CUSTOM_VALUE, filterTitleMobileWidth } from '../constants'
import { useGetOption } from '../hook'
import type { FilterItemProps } from '../types'
import { DateDrawer } from './DateDrawer'
import { NumberDrawer } from './NumberDrawer'

interface TileFilterItemProps
    extends Pick<
        FilterItemProps,
        | 'labelWidth'
        | 'showLabel'
        | 'noFilterLabel'
        | 'config'
        | 'dataSourceList'
        | 'onChange'
        | 'onFetchTextOptions'
        | 'onFetchCascadeOptions'
        | 'labelColor'
        | 'useParseVariableWithSubscription'
    > {
    filterData: FilterBlockValue
    isWrap?: boolean
}

const SCxContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 6px 0 6px 2px;
`

const SCxScrollArea = styled.div<{ isWrap?: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 7px;
    ${({ isWrap }) =>
        isWrap
            ? css`
                  flex-wrap: wrap;
              `
            : css`
                  overflow: auto hidden;
                  ${noButtons('0px')}
                  scrollbar-width: none; /* Firefox */
                  -ms-overflow-style: none; /* IE/Edge */

                  &::-webkit-scrollbar {
                      display: none; /* Chrome, Safari */
                  }
              `}
`

export const TileFilterItem: React.FunctionComponent<TileFilterItemProps> = ({
    config,
    showLabel,
    labelWidth,
    labelColor,
    noFilterLabel,
    dataSourceList,
    filterData,
    isWrap,
    onChange,
    onFetchTextOptions,
    onFetchCascadeOptions,
    useParseVariableWithSubscription
}) => {
    const { filterWay, id, title, selectWay } = config

    const isMultiple = selectWay === FilterSelectWay.multiSelect
    const [opened, setOpened] = useState(false)
    const [cascadeActiveId, setCascadeActiveId] = useState('')
    const { pageTarget } = useApplicationContext()
    const { convertTextByLanguage } = useLanguageContext()
    const { value, customValue } = useMemo(() => filterData?.[id] ?? { value: [], customValue: [] }, [filterData, id])
    const labelStyle = useLabelStyle(labelColor)

    const handleInitChange = useCallback(
        (val: string[]) => {
            onChange?.(id, { type: filterWay, value: val, customValue })
        },
        [onChange, id, filterWay, customValue]
    )

    const { options } = useGetOption({
        value,
        onChange: handleInitChange,
        onFetchTextOptions,
        onFetchCascadeOptions,
        dataSourceList,
        config,
        useParseVariableWithSubscription
    })

    const numberConfig = useMemo(() => {
        if (filterWay === FilterWay.numberFilter) {
            return config
        }
        return null
    }, [config, filterWay])

    const cascadeConfig = useMemo(() => {
        if (filterWay === FilterWay.cascadeFilter) {
            return config
        }
        return null
    }, [config, filterWay])

    const handleValueClear = useCallback(() => {
        onChange?.(id, { type: filterWay, value: [], customValue })
    }, [customValue, filterWay, id, onChange])

    const handleChange = useCallback(
        (option: FilterOption, isActive: boolean) => {
            if (option.children && option.children.length > 0) {
                setCascadeActiveId(option.value)
                return
            }
            if (option.value === CUSTOM_VALUE) {
                setOpened(true)
                return
            }
            if (isMultiple) {
                const newValue = isActive ? value.filter(v => v !== option.value) : [...value, option.value]
                onChange?.(id, { type: filterWay, value: newValue, customValue })
                return
            }
            const newValue = isActive ? [] : [option.value]
            onChange?.(id, { type: filterWay, value: newValue, customValue })
        },
        [customValue, filterWay, id, isMultiple, onChange, value]
    )

    const handleCustomChange = useCallback(
        (_: string[], customVal?: NumberRangeArray) => {
            // const isActive = value?.some(v => v === CUSTOM_VALUE)
            const newValue = customVal ? [...value, CUSTOM_VALUE] : value.filter(v => v !== CUSTOM_VALUE)
            onChange?.(id, { type: filterWay, value: newValue, customValue: customVal })
        },
        [filterWay, id, onChange, value]
    )

    // const a = design?.color

    const customLabel = useMemo(() => {
        if (!customValue || !value?.includes(CUSTOM_VALUE)) {
            return convertTextByLanguage('custom')
        }
        const [start, end] = customValue
        const isDateFilter = filterWay === FilterWay.dateFilter
        const isDateStartValue = isDateValue(start)
        const isDateEndValue = isDateValue(end)
        const startLabel = isDateFilter && isDateStartValue ? lightFormat(start, 'yyyy-MM-dd') : start ?? ''
        const endLabel = isDateFilter && isDateEndValue ? lightFormat(end, 'yyyy-MM-dd') : end ?? ''

        if (isDateStartValue && isDateEndValue) {
            return `${startLabel} ~ ${endLabel}`
        }
        if (isDateStartValue) {
            return `> ${startLabel}`
        }
        if (isDateEndValue) {
            return `< ${endLabel}`
        }
        return convertTextByLanguage('custom')
    }, [customValue, value, filterWay, convertTextByLanguage])

    const drawerEle = useMemo(() => {
        if (filterWay === FilterWay.numberFilter) {
            return (
                <NumberDrawer
                    opened={opened}
                    title={title}
                    target={pageTarget}
                    numberRangeMode={numberConfig?.numberRangeMode}
                    min={numberConfig?.numberRange?.[0]}
                    max={numberConfig?.numberRange?.[1]}
                    step={numberConfig?.step}
                    customValue={customValue}
                    onClose={() => setOpened(false)}
                    onFinish={handleCustomChange}
                />
            )
        }
        if (filterWay === FilterWay.dateFilter) {
            return (
                <DateDrawer
                    opened={opened}
                    title={title}
                    target={pageTarget}
                    customValue={customValue}
                    showCustom
                    onClose={() => setOpened(false)}
                    onFinish={handleCustomChange}
                />
            )
        }
        if (filterWay === FilterWay.cascadeFilter) {
            return (
                <CascadeDrawer
                    opened={Boolean(cascadeActiveId)}
                    value={value}
                    title={title}
                    target={pageTarget}
                    allSelected
                    selectionMode={cascadeConfig?.isLastLevel ? 'leaf-only' : 'auto-leaf'}
                    isMultiple={!cascadeConfig?.isLastLevel}
                    options={options.find(option => option.value === cascadeActiveId)?.children}
                    showFooterBtn
                    onClose={() => setCascadeActiveId('')}
                    // 单独处理onChange
                    onFinish={v => {
                        onChange?.(id, {
                            type: filterWay,
                            value: v,
                            customValue
                        })
                    }}
                />
            )
        }
        return null
    }, [
        cascadeActiveId,
        customValue,
        filterWay,
        cascadeConfig?.isLastLevel,
        handleCustomChange,
        id,
        numberConfig?.numberRange,
        numberConfig?.numberRangeMode,
        numberConfig?.step,
        onChange,
        opened,
        options,
        pageTarget,
        title,
        value
    ])

    return (
        <SCxContainer>
            {showLabel && (
                <TooltipText
                    title={title}
                    render={ref => {
                        return (
                            <Text
                                ref={ref}
                                style={{ width: labelWidth ?? filterTitleMobileWidth, flexShrink: 0, ...labelStyle }}
                                lineHeight="24px"
                                size={14}
                                align="left"
                            >
                                {title}
                            </Text>
                        )
                    }}
                />
            )}
            <SCxScrollArea isWrap={isWrap}>
                {options.length > 0 && (
                    <CheckButton label={noFilterLabel || '全部'} active={!value || value.length === 0} onClick={handleValueClear} />
                )}
                {options.map(option => {
                    const options = [option, ...getCascadeFlatOption(option, [])]
                    const isActive = value.some(v => options.some(option => option.value === v))
                    return (
                        <CheckButton
                            key={option.value}
                            label={option.value === CUSTOM_VALUE ? customLabel : option.label}
                            active={isActive}
                            isBackground
                            onClick={() => handleChange?.(option, isActive)}
                            isSuffixIcon={Boolean(option.children && option.children.length > 0)}
                        />
                    )
                })}
            </SCxScrollArea>
            {drawerEle}
        </SCxContainer>
    )
}
